import request, { IWorkspaceResponse } from '/@/api/http/request'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { ELocalStorageKey } from '/@/types'
import type { DrcExitResult, DrcLeaseStatus } from '/@/utils/drc-lease'

// DRC 链路
const DRC_API_PREFIX = '/control/api/v1'
const workspaceId: string = localStorage.getItem(ELocalStorageKey.WorkspaceId) || ''

export interface PostDrcBody {
  client_id?: string // token过期时，用于续期则必填
  expire_sec?: number // 过期时间，单位秒，默认3600
}

export interface DrcParams {
  address: string
  username: string
  password: string
  client_id: string
  expire_time: number // 过期时间
  enable_tls: boolean // 是否开启tls
}

// 获取 mqtt 连接认证；传入 client_id 时为同一客户端续签凭证（JWT 到期前调用）
export async function postDrc (body: PostDrcBody, options: { skipErrorMessage?: boolean } = {}): Promise<IWorkspaceResponse<DrcParams>> {
  const resp = await request.post(`${DRC_API_PREFIX}/workspaces/${workspaceId}/drc/connect`, body, options)
  return resp.data
}

export interface DrcEnterBody {
  client_id: string
  dock_sn: string
  expire_sec?: number // 过期时间，单位秒，默认3600
  fencing_token?: number
  force_takeover?: boolean
  expected_lease_version?: number
  takeover_wayline?: boolean
  takeover_wayline_job_id?: string
  // 续租/重连：仅当 client_id + fencing_token 仍是当前持有者时成功，返回新的 fencing_token 与 lease_until
  resume_session?: boolean
  device_info?: {
    osd_frequency?: number
    hsi_frequency?: number
  }
}

export interface DrcEnterResp {
  sub: string[] // 需要订阅接收的topic
  pub: string[] // 推送的topic地址
  fencing_token?: number
  lease_until?: number
}

// 进入飞行控制 （建立drc连接&获取云控控制权）
export async function postDrcEnter (body: DrcEnterBody, options: { skipErrorMessage?: boolean } = {}): Promise<IWorkspaceResponse<DrcEnterResp>> {
  const resp = await request.post(`${DRC_API_PREFIX}/workspaces/${workspaceId}/drc/enter`, body, options)
  return resp.data
}

export async function getDrcLeaseStatus (dockSn: string): Promise<IWorkspaceResponse<DrcLeaseStatus>> {
  const resp = await request.get(
    `${DRC_API_PREFIX}/workspaces/${workspaceId}/drc/leases/${encodeURIComponent(dockSn)}`,
    { skipErrorMessage: true }
  )
  return resp.data
}

export interface DrcExitBody {
  client_id: string
  dock_sn: string
  fencing_token?: number
  resume_wayline?: boolean
}

// 退出飞行控制 （退出drc连接&退出云控控制权）
// 后端始终释放租约；data.device_exit_confirmed=false 表示机场未确认退出指令飞行模式
export async function postDrcExit (body: DrcExitBody, options: { skipErrorMessage?: boolean } = {}): Promise<IWorkspaceResponse<DrcExitResult | null>> {
  const resp = await request.post(`${DRC_API_PREFIX}/workspaces/${workspaceId}/drc/exit`, body, options)
  return resp.data
}

// 页面关闭时，普通 XHR 可能被浏览器取消；keepalive 用于尽力完成 DRC 退出。
export function postDrcExitKeepalive (body: DrcExitBody): Promise<Response> {
  const configuredBaseUrl = CURRENT_CONFIG.baseURL || window.location.origin
  const absoluteBaseUrl = new URL(configuredBaseUrl, window.location.origin)
  const exitUrl = new URL(`${DRC_API_PREFIX}/workspaces/${workspaceId}/drc/exit`, absoluteBaseUrl)
  const token = localStorage.getItem(ELocalStorageKey.Token)
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers[ELocalStorageKey.Token] = token
  }
  return window.fetch(exitUrl.toString(), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    keepalive: true,
  })
}
